import * as THREE from 'three';

const positive = (value, fallback = null) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
};

const round = (value, digits = 3) => {
    const multiplier = 10 ** digits;
    return Math.round(Number(value || 0) * multiplier) / multiplier;
};

const normalizeFileType = (value = '') => String(value || '').trim().toLowerCase();

const getRawBounds = (modelData = {}) => {
    const geometry = modelData.geometry;

    if (geometry?.isBufferGeometry) {
        if (!geometry.boundingBox) geometry.computeBoundingBox();

        const box = geometry.boundingBox;
        const size = new THREE.Vector3();

        if (box && !box.isEmpty()) {
            box.getSize(size);
            return {
                x: positive(size.x, 0),
                y: positive(size.y, 0),
                z: positive(size.z, 0),
            };
        }
    }

    return {
        x: positive(modelData.dimensions?.x, 0),
        y: positive(modelData.dimensions?.y, 0),
        z: positive(modelData.dimensions?.z, 0),
    };
};

const rotateToViewerAxes = (raw, fileType) => {
    /*
     * AdminProduct3DViewport поворачивает STL на -90° по X.
     * У OBJ/GLB/GLTF итоговая world-ориентация остаётся без поворота.
     */
    if (normalizeFileType(fileType) === 'stl') {
        return {
            x: raw.x,
            y: raw.z,
            z: raw.y,
        };
    }

    return { ...raw };
};

const detectAxisPreset = (sceneSize) => {
    const x = Math.max(positive(sceneSize.x, 0), 0.000001);
    const y = Math.max(positive(sceneSize.y, 0), 0.000001);
    const z = Math.max(positive(sceneSize.z, 0), 0.000001);
    const max = Math.max(x, y, z);

    /*
     * axisPreset описывает не форму силуэта, а расположение осей модели.
     * horizontal здесь означает, что толщина лежит по Y, а высота/длина по Z.
     */
    if (y <= max * 0.35 && z >= max * 0.65) {
        return 'horizontal';
    }

    return 'vertical';
};

const inferShapeOrientation = (dimensions) => {
    const height = positive(dimensions.height, 0);
    const width = positive(dimensions.width, 0);

    if (!height || !width) return 'unknown';

    if (Math.abs(height - width) / Math.max(height, width) < 0.12) {
        return 'square';
    }

    return width > height * 1.08 ? 'horizontal' : 'vertical';
};

const toSemanticRaw = (sceneSize, axisPreset) => {
    if (axisPreset === 'horizontal') {
        return {
            width: sceneSize.x,
            height: sceneSize.z,
            thickness: sceneSize.y,
        };
    }

    return {
        width: sceneSize.x,
        height: sceneSize.y,
        thickness: sceneSize.z,
    };
};

const normalizeProductDimensions = (value = {}) => ({
    height: positive(value.height, null),
    width: positive(value.width, null),
    thickness: positive(value.thickness, null),
});

const hasDimensions = (value = {}) => (
    positive(value.height, null)
    && positive(value.width, null)
    && positive(value.thickness, null)
);

const rangePenalty = (value, min, max, idealMin, idealMax) => {
    if (!positive(value, null)) return 20;

    if (value < min) return 8 + Math.abs(Math.log(value / min)) * 8;
    if (value > max) return 8 + Math.abs(Math.log(value / max)) * 8;
    if (value < idealMin) return Math.abs(Math.log(value / idealMin)) * 1.8;
    if (value > idealMax) return Math.abs(Math.log(value / idealMax)) * 1.8;

    return 0;
};

const closenessPenalty = (candidate, productDimensions) => {
    if (!hasDimensions(productDimensions)) return 0;

    return (
        Math.abs(Math.log(candidate.height / productDimensions.height)) * 0.9
        + Math.abs(Math.log(candidate.width / productDimensions.width)) * 0.9
        + Math.abs(Math.log(candidate.thickness / productDimensions.thickness)) * 0.45
    );
};

const roundnessPenalty = (value) => {
    const nearestTen = Math.round(value / 10) * 10;
    return Math.abs(value - nearestTen) / 100;
};

const chooseUnitScale = (semanticRaw, productDimensions) => {
    const candidates = [0.001, 0.01, 0.1, 1, 10, 100, 1000];

    return candidates
        .map((unitScale) => {
            const candidate = {
                height: semanticRaw.height * unitScale,
                width: semanticRaw.width * unitScale,
                thickness: semanticRaw.thickness * unitScale,
            };
            const ratio = candidate.thickness / Math.max(
                1,
                Math.min(candidate.height, candidate.width)
            );

            const score = (
                rangePenalty(candidate.height, 250, 4000, 500, 2500)
                + rangePenalty(candidate.width, 180, 4000, 300, 2400)
                + rangePenalty(candidate.thickness, 15, 600, 40, 250)
                + (ratio < 0.01 || ratio > 0.65 ? 8 : 0)
                + closenessPenalty(candidate, productDimensions)
                + roundnessPenalty(candidate.height)
                + roundnessPenalty(candidate.width)
                + roundnessPenalty(candidate.thickness)
            );

            return {
                unitScale,
                candidate,
                score,
            };
        })
        .sort((left, right) => left.score - right.score)[0];
};

const differencePercent = (measured, productDimensions) => {
    if (!hasDimensions(productDimensions)) return null;

    const diffs = [
        Math.abs(measured.height - productDimensions.height) / productDimensions.height,
        Math.abs(measured.width - productDimensions.width) / productDimensions.width,
        Math.abs(measured.thickness - productDimensions.thickness) / productDimensions.thickness,
    ];

    return round(
        diffs.reduce((sum, value) => sum + value, 0) / diffs.length * 100,
        2
    );
};

const getGeometryStats = (geometry) => {
    const position = geometry?.getAttribute?.('position');
    const vertexCount = Number(position?.count || 0);
    const triangleCount = geometry?.index
        ? Math.floor(Number(geometry.index.count || 0) / 3)
        : Math.floor(vertexCount / 3);

    return {
        vertexCount: vertexCount || null,
        triangleCount: triangleCount || null,
    };
};

export const measureCatalogModelGeometry = ({
    modelData,
    productDimensions = {},
}) => {
    if (!modelData?.geometry) {
        throw new Error('3D-геометрия не загружена');
    }

    const rawBounds = getRawBounds(modelData);
    if (![rawBounds.x, rawBounds.y, rawBounds.z].every((value) => value > 0)) {
        throw new Error('У 3D-модели не удалось получить bounding box');
    }

    const sceneBounds = rotateToViewerAxes(rawBounds, modelData.fileType);
    const axisPreset = detectAxisPreset(sceneBounds);
    const semanticRaw = toSemanticRaw(sceneBounds, axisPreset);
    const normalizedProductDimensions = normalizeProductDimensions(productDimensions);
    const chosen = chooseUnitScale(semanticRaw, normalizedProductDimensions);
    const dimensions = {
        height: Math.max(1, Math.round(chosen.candidate.height)),
        width: Math.max(1, Math.round(chosen.candidate.width)),
        thickness: Math.max(1, Math.round(chosen.candidate.thickness)),
        unit: 'mm',
    };
    const diff = differencePercent(dimensions, normalizedProductDimensions);
    const plausibilityScore = Math.max(0, Math.min(1, 1 - chosen.score / 20));
    const agreementScore = diff === null
        ? 0.7
        : Math.max(0.1, Math.min(1, 1 - diff / 45));
    const confidenceScore = round(
        plausibilityScore * 0.6 + agreementScore * 0.4,
        3
    );
    const confidence = confidenceScore >= 0.82
        ? 'Высокая'
        : (confidenceScore >= 0.62 ? 'Средняя' : 'Низкая');
    const warnings = [];

    if (diff !== null && diff > 15) {
        warnings.push(`Размер 3D отличается от размера товара в среднем на ${diff}%`);
    }
    if (confidence === 'Низкая') {
        warnings.push('Единицы или ориентацию модели нужно проверить вручную');
    }
    if (dimensions.thickness > Math.min(dimensions.height, dimensions.width) * 0.4) {
        warnings.push('Толщина включает выступающий декор или модель имеет нестандартную ориентацию');
    }

    return {
        dimensions,
        productDimensions: {
            ...normalizedProductDimensions,
            unit: 'mm',
        },
        rawDimensions: {
            x: round(rawBounds.x, 6),
            y: round(rawBounds.y, 6),
            z: round(rawBounds.z, 6),
        },
        outerDimensions: {
            x: round(sceneBounds.x * chosen.unitScale, 3),
            y: round(sceneBounds.y * chosen.unitScale, 3),
            z: round(sceneBounds.z * chosen.unitScale, 3),
            unit: 'mm',
        },
        axisPreset,
        orientation: inferShapeOrientation(dimensions),
        unitScale: chosen.unitScale,
        confidence,
        confidenceScore,
        differencePercent: diff,
        fileType: normalizeFileType(modelData.fileType),
        ...getGeometryStats(modelData.geometry),
        warnings,
    };
};

export default measureCatalogModelGeometry;

const disposeMaterial = (material) => {
    if (!material) return;

    if (Array.isArray(material)) {
        material.forEach(disposeMaterial);
        return;
    }

    [
        'map',
        'normalMap',
        'roughnessMap',
        'metalnessMap',
        'aoMap',
        'emissiveMap',
        'alphaMap',
        'bumpMap',
        'displacementMap',
    ].forEach((key) => material[key]?.dispose?.());

    material.dispose?.();
};

export const disposeCatalogModelData = (modelData = {}) => {
    modelData.geometry?.dispose?.();
    disposeMaterial(modelData.materials);

    (Array.isArray(modelData.resourceUrls) ? modelData.resourceUrls : [])
        .forEach((url) => {
            if (typeof url === 'string' && url.startsWith('blob:')) {
                window.URL.revokeObjectURL(url);
            }
        });
};
